import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    Box, Paper, TextField, Button, Typography, Link,
    Alert, InputAdornment, CircularProgress,
} from '@mui/material';
import {
    Person as PersonIcon,
    Email as EmailIcon,
    Lock as LockIcon,
    CalendarMonth as LogoIcon,
} from '@mui/icons-material';
import { auth } from '../services/api';

const Register = () => {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({ username: '', email: '', password: '', confirmPassword: '' });
    const [error,    setError]    = useState('');
    const [loading,  setLoading]  = useState(false);

    const handleChange = e => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (formData.password !== formData.confirmPassword) { setError('Passwords do not match.'); return; }
        setLoading(true);
        try {
            const pendingTokens = JSON.parse(localStorage.getItem('pendingEventTokens') || '[]');
            await auth.register({
                username: formData.username,
                email: formData.email,
                password: formData.password,
                shareTokens: pendingTokens,
            });
            localStorage.removeItem('pendingEventTokens');
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.message || err.response?.data || 'An error occurred during registration.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight:'100vh', bgcolor:'#F8FAFC', display:'flex', alignItems:'center', justifyContent:'center', px:2, py:6 }}>
            <Box sx={{ width:'100%', maxWidth:420 }}>

                <Paper elevation={0} variant="outlined" sx={{ borderRadius:4, overflow:'hidden', borderColor:'rgba(0,0,0,0.1)' }}>

                    {/* Brand header */}
                    <Box sx={{ background:'linear-gradient(135deg,#7C3AED 0%,#4F46E5 100%)', px:4, pt:5, pb:4, position:'relative', overflow:'clip' }}>
                        <Box sx={{ position:'absolute', top:-30, right:-20, width:160, height:160, borderRadius:'50%', background:'rgba(255,255,255,0.07)', pointerEvents:'none' }} />
                        <Box sx={{ position:'absolute', bottom:-40, left:-10, width:120, height:120, borderRadius:'50%', background:'rgba(255,255,255,0.05)', pointerEvents:'none' }} />
                        <Box sx={{ position:'relative', zIndex:1, display:'flex', flexDirection:'column', gap:2 }}>
                            <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
                                <Box sx={{ width:40, height:40, borderRadius:2, bgcolor:'rgba(255,255,255,0.18)', border:'1.5px solid rgba(255,255,255,0.3)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                    <LogoIcon sx={{ fontSize:22, color:'white' }} />
                                </Box>
                                <Typography variant="h6" fontWeight={800} sx={{ color:'white', letterSpacing:-0.3 }}>Quuip</Typography>
                            </Box>
                            <Box>
                                <Typography variant="h5" fontWeight={800} sx={{ color:'white', lineHeight:1.2 }}>Create your account</Typography>
                                <Typography variant="body2" sx={{ color:'rgba(255,255,255,0.75)', mt:0.5 }}>Free forever — no credit card needed</Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Form */}
                    <Box sx={{ px:4, py:4, bgcolor:'white', display:'flex', flexDirection:'column', gap:2.5 }}>
                        {error && <Alert severity="error" sx={{ borderRadius:2 }}>{error}</Alert>}

                        <Box component="form" onSubmit={handleSubmit} sx={{ display:'flex', flexDirection:'column', gap:2.5 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:0.75, fontSize:'0.65rem' }}>Username</Typography>
                                <TextField fullWidth required size="small" name="username" autoComplete="username" autoFocus
                                    placeholder="Choose a username"
                                    value={formData.username} onChange={handleChange}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize:17, color:'text.disabled' }} /></InputAdornment> }} />
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:0.75, fontSize:'0.65rem' }}>Email</Typography>
                                <TextField fullWidth required size="small" name="email" type="email" autoComplete="email"
                                    placeholder="Your email address"
                                    value={formData.email} onChange={handleChange}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize:17, color:'text.disabled' }} /></InputAdornment> }} />
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:0.75, fontSize:'0.65rem' }}>Password</Typography>
                                <TextField fullWidth required size="small" name="password" type="password" autoComplete="new-password"
                                    placeholder="Create a password"
                                    value={formData.password} onChange={handleChange}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon sx={{ fontSize:17, color:'text.disabled' }} /></InputAdornment> }} />
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:0.75, fontSize:'0.65rem' }}>Confirm Password</Typography>
                                <TextField fullWidth required size="small" name="confirmPassword" type="password" autoComplete="new-password"
                                    placeholder="Repeat your password"
                                    value={formData.confirmPassword} onChange={handleChange}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon sx={{ fontSize:17, color:'text.disabled' }} /></InputAdornment> }} />
                            </Box>

                            <Button type="submit" fullWidth variant="contained" size="large" disabled={loading}
                                sx={{ fontWeight:700, borderRadius:2.5, py:1.5, fontSize:'1rem', background:'linear-gradient(135deg,#7C3AED,#4F46E5)', boxShadow:'0 4px 14px rgba(124,58,237,0.3)', '&:hover':{ boxShadow:'0 6px 20px rgba(124,58,237,0.45)' }, mt:0.5 }}>
                                {loading ? <CircularProgress size={20} sx={{ color:'white' }} /> : 'Create Account'}
                            </Button>
                        </Box>

                        <Box sx={{ pt:2, textAlign:'center', borderTop:'1px solid', borderColor:'divider' }}>
                            <Typography variant="body2" color="text.secondary">
                                Already have an account?{' '}
                                <Link component={RouterLink} to="/login"
                                    sx={{ color:'#7C3AED', fontWeight:700, textDecoration:'none', '&:hover':{ textDecoration:'underline' } }}>
                                    Sign in
                                </Link>
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default Register;
